import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    AlertTriangle,
    ArrowDownRight,
    ArrowUpRight,
    BarChart3,
    Bell,
    CalendarRange,
    Gauge,
    Minus,
    ShieldAlert,
    TrendingDown,
    TrendingUp,
    Users,
    Zap,
} from "lucide-react";
import { InteractiveAreaChart } from "./statusHelpers";
import { myLocalStorage } from "../../StorageHelper";
import { NGROK } from "../../../APIs";
import axios from "axios";
import "./AlertTrendPanel.css";

// ─── Types ──────────────────────────────────────────────

interface DayBucket {
    label: string;
    alertCount: number;
}

/** Pre-computed payload returned by GET /api/cloud/alert-trend */
interface AlertTrend {
    hasData: boolean;
    trend: "improving" | "worsening" | "stable";
    buckets: DayBucket[];
    latest: number;
    previous: number;
    oldest: number;
    delta: number;
    overallDelta: number;
    maxCount: number;
    minCount: number;
    avgCount: number;
    peakLabel: string;
    lowLabel: string;
    maxJump: number;
    maxJumpDay: string;
    volatilityPct: number;
    totalAlerts?: number;
    activeDays?: number;
    previousWeekTotal?: number;
    weekChangePct?: number | null;
    avgDaily?: number;
    peakDayCount?: number;
    criticalCount?: number;
    highCount?: number;
    mediumCount?: number;
    lowCount?: number;
    uniqueEntities?: number;
    topEntity?: string | null;
    topEntityCount?: number;
}

interface AlertTrendPanelProps {
    cloudProvider: string;
    /** Which entities' alerts to trend: USER (human users), SERVICE_ACCOUNT (NHI / IAM roles / Azure identities), or ALL. */
    scope?: "USER" | "SERVICE_ACCOUNT" | "ALL";
    onCompare?: () => void;
    onLoadingChange?: (loading: boolean) => void;
}

// ─── Helpers ────────────────────────────────────────────

/** Map a backend bucket label to the chart's x-axis label ("Today" -> "TODAY", "6d ago" -> "6D"). */
function toChartLabel(label: string): string {
    return label === "Today" ? "TODAY" : label.split(" ")[0].toUpperCase();
}

function toDayText(label: string): string {
    if (label === "Today") return "today";
    const days = parseInt(label, 10);
    if (days === 1) return "yesterday";
    return Number.isNaN(days) ? label : `${days} days ago`;
}

function plural(count: number, word: string, pluralWord = `${word}s`): string {
    return `${count} ${count === 1 ? word : pluralWord}`;
}

function numberOr(value: unknown, fallback: number): number {
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function formatNumber(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

const CHART_VIEW_WIDTH = 600;
const MIN_CHART_VIEW_HEIGHT = 150;

// ─── Component ──────────────────────────────────────────

export default function AlertTrendPanel({ cloudProvider, scope = "ALL", onLoadingChange }: AlertTrendPanelProps) {
    const [data, setData] = useState<AlertTrend | null>(null);
    const [loading, setLoading] = useState(true);
    const chartRef = useRef<HTMLDivElement>(null);
    const [chartViewHeight, setChartViewHeight] = useState(240);

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useLayoutEffect(() => {
        const element = chartRef.current;
        if (!element) return;
        const update = () => {
            const { width, height } = element.getBoundingClientRect();
            if (width <= 0 || height <= 0) return;
            setChartViewHeight(Math.max(MIN_CHART_VIEW_HEIGHT, Math.round((CHART_VIEW_WIDTH * height) / width)));
        };
        update();
        if (!window.ResizeObserver) return;
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, [loading, data]);

    useEffect(() => {
        const tenantName =
            myLocalStorage.getItem("latestTenant")?.tenantName ||
            myLocalStorage.getItem("latestTenant");

        const cp = cloudProvider?.toLowerCase();
        let cloudId: string | null = null;
        if (cp === "gcp") {
            cloudId = myLocalStorage.getItem("gcpSelectedProjectId");
        } else if (cp === "aws") {
            cloudId = myLocalStorage.getItem("selectedAwsAccount");
        } else if (cp === "azure") {
            const sub = myLocalStorage.getItem("latestSubList");
            cloudId = sub?.azureSubscriptionId || null;
        }

        if (!tenantName || !cloudId) {
            setLoading(false);
            return;
        }

        const fetchAlertTrend = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${NGROK}/api/cloud/alert-trend`, {
                    params: {
                        tenantName,
                        cloudId,
                        cloudType: cloudProvider?.toUpperCase(),
                        scope,
                    },
                });
                setData(response.data as AlertTrend);
            } catch {
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchAlertTrend();
    }, [cloudProvider, scope]);

    // ── Loading state ───────────────────────────────────
    if (loading) {
        return (
            <div className="snapshot-comparison">
                <div className="sc-insights" style={{ flex: 1 }}>
                    <div className="sc-insights-header">
                        <AlertTriangle className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                        <span className="sc-insights-title">Alert Trend Analysis · Last 7 Days</span>
                    </div>
                    <div className="sc-insight-items">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="h-4 bg-muted/50 animate-pulse rounded-md mb-2" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const buckets = data?.buckets ?? [];
    const chartValues = buckets.map((b) => b.alertCount);
    const chartLabels = buckets.map((b) => toChartLabel(b.label));

    // ── No data state ───────────────────────────────────
    // When there are zero alerts / no trend data, hide the card entirely.
    if (!data || !data.hasData) {
        return null;
    }

    // ── Full data view (all figures pre-computed by the backend) ──
    const { trend, latest, delta, maxCount, minCount, peakLabel, lowLabel, maxJump, maxJumpDay, topEntity } = data;
    const weekSum = chartValues.reduce((sum, count) => sum + count, 0);
    const totalAlerts = numberOr(data.totalAlerts, weekSum);
    const activeDays = numberOr(data.activeDays, chartValues.filter((count) => count > 0).length);
    const avgDaily = numberOr(data.avgDaily, chartValues.length ? Math.round((weekSum / chartValues.length) * 10) / 10 : 0);
    const peakDayCount = numberOr(data.peakDayCount, chartValues.filter((count) => count === maxCount).length);
    const hasWeekComparison = typeof data.previousWeekTotal === "number";
    const previousWeekTotal = numberOr(data.previousWeekTotal, 0);
    const weekChangePct = typeof data.weekChangePct === "number" ? data.weekChangePct : null;
    const hasSeverity = typeof data.criticalCount === "number";
    const criticalCount = numberOr(data.criticalCount, 0);
    const highCount = numberOr(data.highCount, 0);
    const mediumCount = numberOr(data.mediumCount, 0);
    const lowCount = numberOr(data.lowCount, 0);
    const hasEntities = typeof data.uniqueEntities === "number";
    const uniqueEntities = numberOr(data.uniqueEntities, 0);
    const topEntityCount = numberOr(data.topEntityCount, 0);
    const todayVsAverage = Math.round((latest - avgDaily) * 10) / 10;

    return (
        <div className="snapshot-comparison">
            <div className="sc-insights">
                <div className="sc-insights-header">
                    <AlertTriangle className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                    <span className="sc-insights-title">Alert Trend Analysis · Last 7 Days</span>
                    <div className={`sc-trend-badge ${trend}`}>
                        {trend === "improving" ? (
                            <><TrendingDown /> Improving</>
                        ) : trend === "worsening" ? (
                            <><TrendingUp /> Worsening</>
                        ) : (
                            <><Minus /> Stable</>
                        )}
                    </div>
                </div>

                <div className="sc-insight-items">
                    <div className="sc-insight-item">
                        <Bell />
                        <span>
                            Today has <strong>{plural(latest, "alert")}</strong>
                            {delta === 0 ? (
                                <> — same as yesterday</>
                            ) : (
                                <>
                                    {" "}
                                    <span className={`sc-delta ${delta > 0 ? "positive" : "negative"}`}>
                                        {delta > 0 ? (
                                            <><ArrowUpRight className="inline w-3 h-3" /> +{delta}</>
                                        ) : (
                                            <><ArrowDownRight className="inline w-3 h-3" /> {delta}</>
                                        )}
                                    </span>
                                    {" "}vs yesterday
                                </>
                            )}
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <BarChart3 />
                        <span>
                            <strong>{plural(totalAlerts, "alert")}</strong> this week, raised on <strong>{activeDays} of 7</strong> days
                        </span>
                    </div>

                    {hasWeekComparison && (
                        <div className="sc-insight-item">
                            <CalendarRange />
                            <span>
                                {weekChangePct === null ? (
                                    <>No alerts in the previous 7 days — all of this week's alerts are new</>
                                ) : weekChangePct === 0 ? (
                                    <>Same volume as the previous 7 days (<strong>{plural(previousWeekTotal, "alert")}</strong>)</>
                                ) : (
                                    <>
                                        <strong>{weekChangePct > 0 ? "+" : ""}{weekChangePct}%</strong> vs the previous 7 days (<strong>{plural(previousWeekTotal, "alert")}</strong>)
                                    </>
                                )}
                            </span>
                        </div>
                    )}

                    <div className="sc-insight-item">
                        <Zap />
                        <span>
                            Busiest day: <strong>{plural(maxCount, "alert")}</strong> ({toDayText(peakLabel)}
                            {peakDayCount > 1 && <> and {plural(peakDayCount - 1, "other day")}</>}) · Quietest: <strong>{plural(minCount, "alert")}</strong> ({toDayText(lowLabel)})
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <Gauge />
                        <span>
                            Daily average: <strong>{formatNumber(avgDaily)} alerts</strong>
                            {todayVsAverage > 0
                                ? <> — today is <strong>{formatNumber(todayVsAverage)} above</strong> average</>
                                : todayVsAverage < 0
                                    ? <> — today is <strong>{formatNumber(Math.abs(todayVsAverage))} below</strong> average</>
                                    : <> — today is at the average</>
                            }
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <TrendingUp />
                        <span>
                            {maxJump > 0
                                ? <>Largest day-over-day jump: <strong>+{plural(maxJump, "alert")}</strong> ({toDayText(maxJumpDay)})</>
                                : <>No day-over-day increase in alerts this week</>
                            }
                        </span>
                    </div>

                    {hasSeverity && (
                        <div className="sc-insight-item">
                            <ShieldAlert />
                            <span>
                                Severity: <strong>{criticalCount} critical</strong> · <strong>{highCount} high</strong> · <strong>{mediumCount} medium</strong> · <strong>{lowCount} low</strong>
                            </span>
                        </div>
                    )}

                    {hasEntities && (
                        <div className="sc-insight-item">
                            <Users />
                            <span>
                                {topEntity ? (
                                    <>
                                        <strong>{plural(uniqueEntities, "entity", "entities")}</strong> alerted · most: <strong className="sc-entity" title={topEntity}>{topEntity}</strong> ({plural(topEntityCount, "alert")})
                                    </>
                                ) : (
                                    <>No entity details recorded for this week's alerts</>
                                )}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="sc-divider" />

            {/* Right: Alert Trend Chart */}
            <div className="sc-scores">
                <div ref={chartRef} className="sc-chart-wrapper">
                    <InteractiveAreaChart
                        values={chartValues}
                        labels={chartLabels}
                        color="hsl(var(--primary))"
                        gradientId="sc-trend-chart-gradient"
                        width={CHART_VIEW_WIDTH}
                        height={chartViewHeight}
                    />
                </div>
            </div>
        </div>
    );
}
